import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import {
    trackAsImpersonation,
    trackGaLoginEvent,
    trackGaMyAccountFlyOutEvent,
    trackGaMyAccountPagesNavEvent,
    trackGaToolbarNavClickEvent,
} from "@stoneage/App/Utilities/DataLayer";
import Tooltip from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    links = "links",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const userName = state.context.session.userName;
    const { search } = getLocation(state);
    const pageType = getCurrentPage(state).type;
    const requestAccountPageLink = getPageLinkByPageType(state, "RequestAccountPage");
    const billToState = getBillToState(state, state.context.session.billToId);
    let billTo;
    if (billToState && billToState.value) {
        billTo = billToState.value;
    }
    const salesPersonName = state.context.session?.properties?.salesPersonName || "";
    const salesPersonPhone = state.context.session?.properties?.salesPersonPhone || "";
    const session = state.context.session;

    const locationFinderUrl = getPageLinkByPageType(state, "LocationFinderPage")?.url;
    const customSettings = getSettingsCollection(state).customSettings as any;
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        isLoading: state.context.isSigningIn || state.pages.signIn.isSigningInAsGuest,
        userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // customization
        session,
        selectedBillToId: state.context.session.billToId,
        billToState,
        billTo,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        isSticky: state.xHeader.isSticky,
        isHomePage: pageType === "HomePage", // HomePage
        toggleHeaderColor: state.xHeader.toggleHeaderColor,
        createAccountUrl: requestAccountPageLink ? `${requestAccountPageLink.url}${search}` : undefined,
        salesPersonName,
        salesPersonPhone,
        customSettings,
        locationFinderUrl,
        myAccountPagesLinks: {
            orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage")?.url,
            invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage")?.url,
            myQuotesPageLink: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
            myProfilePageLink: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            userAdministrationPageLink: getPageLinkByPageType(state, "UserListPage")?.url,
            sentinelPageLink: customSettings?.sentinelHeaderUrl,
            courseManagementPageLink: getPageLinkByPageType(state, "CourseManagementPage")?.url,
            myCoursesPageLink: getPageLinkByPageType(state, "MyCoursesPage")?.url,
        },
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    loadBillTo,
    setNavDrawerIsOpen,
};

export interface HeaderSignInStyles {
    titleClickable?: ClickablePresentationProps;
    accountOverlayGridItem?: GridItemProps;
    accountOverlayLabel?: TypographyPresentationProps;
    accountOverlayValue?: TypographyPresentationProps;
    accountOverlayLink?: ClickablePresentationProps;
    signOutTypography?: TypographyPresentationProps;
    signOutIcon?: IconPresentationProps;
    pricingToggleIcon?: IconPresentationProps;
    inventoryToggleIcon?: IconPresentationProps;
    pricingToggleClickable?: ClickablePresentationProps;
    inventoryToggleClickable?: ClickablePresentationProps;
    userElementIcon?: IconPresentationProps;
    mobileElementIcon?: IconPresentationProps;
    signInOverlayContainer?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    titleClickable: {
        css: css`
            transition: 0.2s;
            &.toggle {
                span {
                    color: ${getColor("secondary")};
                }

                svg,
                .link-icon svg {
                    color: ${getColor("common.ui6")};
                }

                &:hover,
                &:focus {
                    &,
                    span,
                    svg {
                        color: ${getColor("primary")};
                    }
                }
            }
            .link-text {
                font-size: 12px;
                line-height: 13px;
                color: ${getColor("common.white")};
                font-weight: 400;
            }

            .link-icon {
                &,
                svg {
                    height: 14px;
                    width: 14px;
                }
                svg {
                    color: ${getColor("common.white")};
                }
            }

            .account-icon {
                margin-bottom: 3px;
                &,
                svg {
                    height: 16px;
                    width: 16px;
                }
                svg {
                    color: ${getColor("common.ui5")};
                }
                &.toggle svg {
                    color: ${getColor("common.ui6")};
                }
            }

            &.open {
                .arrow {
                    transform: rotate(-180deg);
                    transition: 0.2s;
                }
            }
        `,
    },
    accountOverlayLabel: {
        size: "10px",
        lineHeight: "11px",
        weight: 600,
        css: css`
            letter-spacing: 1px;
            color: ${getColor("secondary")};
            margin-bottom: 8px;
        `,
    },
    accountOverlayValue: {
        size: "14px",
        lineHeight: "22px",
        weight: 400,
    },
    accountOverlayGridItem: {
        css: css`
            flex-direction: column;
        `,
    },
    accountOverlayLink: {
        css: css`
            &,
            span {
                font-size: 13px;
                font-weight: 500;
                line-height: 14px;
            }

            &:hover,
            &:focus {
                color: ${getColor("primary")};
            }
        `,
    },
    signOutTypography: {
        weight: 600,
        color: " secondary",
        size: 12,
    },
    signOutIcon: {
        size: 14,
        src: "IconSignout",
        color: "primary",
        css: css`
            margin-right: 8px;
        `,
    },
    userElementIcon: {
        size: 23,
        src: "IconUser",
        css: css`
            margin-right: 6px;
        `,
    },
    mobileElementIcon: {
        size: 20,
        src: "IconUser",
        color: "common.ui6",
    },
    signInOverlayContainer: {
        css: css`
            button.external_provider_button {
                margin: 20px 0;
            }
        `,
    },
    pricingToggleIcon: {
        size: 14,
        color: "primary",
        css: css`
            margin-right: 8px;
        `,
    },
    inventoryToggleIcon: {
        size: 14,
        color: "primary",
        css: css`
            margin-right: 8px;
        `,
    },
    inventoryToggleClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
    pricingToggleClickable: {
        css: css`
            margin-right: 20px;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: { [fields.links]: LinkModel[]; [fields.showPricingToggle]: boolean; [fields.showInventoryToggle]: boolean };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    isSticky,
    session,
    selectedBillToId,
    billToState,
    billTo,
    loadBillTo,
    isHomePage,
    createAccountUrl,
    salesPersonName,
    salesPersonPhone,
    toggleHeaderColor,
    customSettings,
    locationFinderUrl,
    setNavDrawerIsOpen,
    navDrawerIsOpen,
    myAccountPagesLinks,
    impersonation,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const [myAccountTippy, setTippyInstance] = React.useState<any>();
    const [isOpen, setIsOpen] = React.useState(false);

    React.useEffect(() => {
        // load current billTo
        if (!billToState.value && !billToState.isLoading && selectedBillToId && session.isAuthenticated) {
            loadBillTo({ billToId: selectedBillToId });
        }
    }, []);

    React.useEffect(() => {
        if (billTo) {
            // XNG-Change: track GA4 - Because we are not using the Opti SignIn, we trigger the ga4 login event here and
            // the event tag will be configured to be catched only once per session
            trackGaLoginEvent(billTo.customerNumber, impersonation);
        }
    }, [billTo]);

    if (!myAccountPageLink) {
        return null;
    }

    const { showInventoryToggle, showPricingToggle } = fields;

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            // XNG-Change: track GA4
            trackGaToolbarNavClickEvent(translate("Sign In"), impersonation);
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
                if (typeof window !== "undefined") {
                    window.localStorage.setItem("returnUrl", `returnUrl=${encodeURIComponent(returnUrl)}`);
                }
            }
        }
    };

    const createNewAccountHandler = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
        myAccountTippy && myAccountTippy.hide();
    };

    const mobileElementClickHandler = (e: any) => {
        if (navDrawerIsOpen) {
            setNavDrawerIsOpen({ navDrawerIsOpen: false });
        }
        if (userName && !currentUserIsGuest) {
            history.push(myAccountPageLink?.url as string);
        } else {
            onSignInHandler(e);
        }
    };

    const isSentinelUser = session?.properties?.isSentinelUser === "true" || false;
    const isThinkificUser = session?.properties?.isThinkificUser === "true" || false;
    const sentinelURL = customSettings?.sentinelHeaderUrl;
    const thinkificlURL = customSettings?.thinkificHeaderUrl;

    const SignInElement = (
        <StyledWrapper className="signin-element" {...styles.userElementStyle}>
            <Clickable
                {...styles.titleClickable}
                data-test-selector="header_signIn"
                aria-label="sign in"
                className={`title-clickable  ${isOpen && "open"} ${
                    (isSticky || !isHomePage || toggleHeaderColor) && "toggle"
                }`}
                href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                onClick={onSignInHandler}
                {...({ isSticky, isHomePage, toggleHeaderColor } as any)}
            >
                <Typography
                    data-test-selector="header_userName"
                    className={isSticky || !isHomePage || toggleHeaderColor ? "link-text toggle" : "link-text"}
                >
                    {translate("Sign In")}
                </Typography>
                <Icon
                    size={23}
                    src="ChevronDown"
                    className={
                        isSticky || !isHomePage || toggleHeaderColor ? "link-icon arrow toggle" : "link-icon arrow"
                    }
                    css={css`
                        margin-left: 4px;
                    `}
                />
            </Clickable>
        </StyledWrapper>
    );

    const SignInOverlay = () => (
        <StyledWrapper>
            <StyledWrapper {...styles.signInOverlayContainer}>
                <Typography
                    variant="h4"
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    {translate("Sign In")}
                </Typography>
                <Typography
                    size={12}
                    lineHeight="18px"
                    as="div"
                    css={css`
                        margin-bottom: 16px;
                    `}
                >
                    {siteMessage("Custom_SignInOverlay_WelcomeMsg")}
                </Typography>
                {createWidgetElement("SignIn/SignInExternalProviders", { fields: {} })}
            </StyledWrapper>
            <StyledWrapper
                css={css`
                    margin-top: 19px;
                    padding-top: 19px;
                    border-top: 1px solid ${getColor("common.ui8")};
                `}
            >
                <Typography
                    variant="h4"
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    {translate("Request An Account")}
                </Typography>
                <Typography
                    size={12}
                    lineHeight="18px"
                    as="div"
                    css={css`
                        margin-bottom: 16px;
                    `}
                >
                    {siteMessage("Custom_SignInOverlay_RequestAccountMsg")}
                </Typography>
                <Button onClick={createNewAccountHandler} variant="secondary">
                    {translate("Request An Account")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );

    const UserElement = (
        <StyledWrapper className="user-element" {...styles.userElementStyle}>
            <Clickable
                {...styles.titleClickable}
                data-test-selector="header_signIn"
                aria-label="my account"
                className={`title-clickable  ${isOpen && "open"} ${
                    (isSticky || !isHomePage || toggleHeaderColor) && "toggle"
                }`}
                onClick={() => {
                    // XNG-Change: track GA4
                    if (myAccountPageLink?.url) {
                        trackGaMyAccountFlyOutEvent(
                            myAccountPageLink.url || "",
                            translate("Account"),
                            impersonation,
                            billTo?.customerNumber,
                        );
                        history.push(myAccountPageLink?.url as string);
                    }
                }}
                {...({ isSticky, isHomePage, toggleHeaderColor } as any)}
            >
                <Icon
                    className={
                        isSticky || !isHomePage || toggleHeaderColor
                            ? "account-icon link-icon toggle"
                            : "account-icon link-icon"
                    }
                    {...styles.userElementIcon}
                />
                <Typography
                    data-test-selector="header_userName"
                    className={isSticky || !isHomePage || toggleHeaderColor ? "link-text toggle" : "link-text"}
                >
                    {translate("Account")}
                </Typography>
                <Icon
                    size={23}
                    src="ChevronDown"
                    className={
                        isSticky || !isHomePage || toggleHeaderColor ? "link-icon arrow toggle" : "link-icon arrow"
                    }
                    css={css`
                        margin-left: 4px;
                    `}
                />
            </Clickable>
        </StyledWrapper>
    );

    const MyAccountOverlay = () => (
        <StyledWrapper>
            <StyledWrapper
                css={css`
                    padding-bottom: 23px;
                    margin-bottom: 23px;
                    border-bottom: 1px solid ${getColor("common.ui5")};
                    display: flex;
                    flex-wrap: wrap;
                `}
            >
                <GridContainer gap={15}>
                    {fields.links?.map((fieldLink, index) => {
                        const { overriddenTitle, destination, openInNewTab } = (fieldLink as any).fields;
                        const linkValue = useGetLink(destination);
                        if (!linkValue?.url || (linkValue as any).excludeFromNavigation) {
                            return null;
                        }
                        if (
                            (linkValue?.url === sentinelURL && !isSentinelUser) ||
                            (linkValue?.url === thinkificlURL && !isThinkificUser)
                        ) {
                            return null;
                        }
                        return (
                            <GridItem
                                width={[12, 6, 6, 6, 6]}
                                css={css`
                                    margin-bottom: 10px;
                                `}
                                key={`${overriddenTitle}_${destination.value}${index}`}
                            >
                                <Clickable
                                    href={linkValue.url}
                                    target={openInNewTab ? "_blank" : "_self"}
                                    onClick={() => {
                                        // XNG-Change: track GA4
                                        if (linkValue.url) {
                                            trackGaMyAccountFlyOutEvent(
                                                linkValue.url,
                                                overriddenTitle || linkValue?.title,
                                                impersonation,
                                                billTo?.customerNumber,
                                            );
                                            trackGaMyAccountPagesNavEvent(
                                                null,
                                                linkValue.url,
                                                overriddenTitle || linkValue?.title,
                                                impersonation,
                                                billTo?.customerNumber,
                                                myAccountPagesLinks,
                                            );
                                        }
                                    }}
                                    {...styles.accountOverlayLink}
                                >
                                    {overriddenTitle || linkValue?.title}
                                </Clickable>
                            </GridItem>
                        );
                    })}
                </GridContainer>
            </StyledWrapper>

            <GridContainer gap={15}>
                {billTo && billTo?.companyName && (
                    <GridItem width={[12, 6, 6, 6, 6]} {...styles.accountOverlayGridItem}>
                        <Typography {...styles.accountOverlayLabel}>{translate("CUSTOMER")}</Typography>
                        <Typography {...styles.accountOverlayValue} translate="no">
                            {billTo?.companyName}
                        </Typography>
                    </GridItem>
                )}
                {(salesPersonName || salesPersonPhone) && (
                    <GridItem width={[12, 6, 6, 6, 6]} {...styles.accountOverlayGridItem}>
                        <Typography {...styles.accountOverlayLabel}>{translate("SALES REP")}</Typography>
                        <Typography {...styles.accountOverlayValue} translate="no">
                            {salesPersonName && (
                                <StyledWrapper
                                    css={css`
                                        margin-bottom: 12px;
                                    `}
                                >
                                    {salesPersonName}
                                </StyledWrapper>
                            )}
                            {salesPersonPhone && (
                                <StyledWrapper>
                                    <a href={`tel:${salesPersonPhone}`}>{salesPersonPhone}</a>
                                </StyledWrapper>
                            )}
                        </Typography>
                    </GridItem>
                )}
                <GridItem width={[12, 6, 6, 6, 6]} {...styles.accountOverlayGridItem}>
                    <Typography {...styles.accountOverlayLabel}>{translate("ACCOUNT NUMBER")}</Typography>
                    <Typography {...styles.accountOverlayValue} translate="no">
                        {billTo?.customerNumber}
                    </Typography>
                </GridItem>
            </GridContainer>
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: flex-end;
                    margin-top: 25px;
                `}
            >
                {showInventoryToggle && displayPricingAndInventory && (
                    <Clickable
                        {...styles.inventoryToggleClickable}
                        onClick={() => setShowInventory({ showInventory: !showInventory })}
                    >
                        <Icon {...styles.inventoryToggleIcon} src={showInventory ? EyeOff : Eye} />
                        {translate("Inventory")}
                    </Clickable>
                )}
                {showPricingToggle && displayPricingAndInventory && (
                    <Clickable
                        {...styles.pricingToggleClickable}
                        onClick={() => setShowPricing({ showPricing: !showPricing })}
                    >
                        <Icon {...styles.pricingToggleIcon} src={showPricing ? EyeOff : Eye} />
                        {translate("Pricing")}
                    </Clickable>
                )}
                <Clickable data-test-selector="header_signout" aria-label="sign out" onClick={onSignOutHandler}>
                    <Icon {...styles.signOutIcon}></Icon>
                    <Typography {...styles.signOutTypography}>{translate("Sign Out")}</Typography>
                </Clickable>
            </StyledWrapper>
        </StyledWrapper>
    );

    const MobileElement = () => (
        <StyledWrapper className="mobile-element">
            <Clickable
                data-test-selector="header_signIn"
                aria-label="sign in"
                className="mobile-title-clickable"
                onClick={() => {
                    // eslint-disable-next-line no-restricted-globals
                    mobileElementClickHandler(event);
                }}
            >
                <Icon {...styles.mobileElementIcon} />
                <Hidden below="sm">
                    <Typography
                        color="secondary"
                        size={12}
                        lineHeight="14px"
                        css={css`
                            margin-left: 7px;
                        `}
                    >
                        {userName && !currentUserIsGuest ? translate("Account") : translate("Sign In")}
                    </Typography>
                </Hidden>
            </Clickable>
        </StyledWrapper>
    );

    return (
        <StyledWrapper>
            <Hidden below="lg">
                {userName && !currentUserIsGuest ? (
                    <StyledWrapper>
                        {/* My Account */}
                        <Tooltip
                            trigger="mouseenter focus"
                            onOpen={({ instance }: any) => {
                                setTippyInstance(instance);
                                setIsOpen(true);
                            }}
                            onClose={({ instance }: any) => {
                                setTippyInstance(instance);
                                setIsOpen(false);
                            }}
                            placement="bottom-end"
                            triggerAltText={translate("My Account Menu")}
                            triggerComponent={UserElement}
                            delay={[100, 350]}
                            offset={[0, 8]}
                            cssOverrides={{
                                tooltipBody: css``,
                                tooltipContainer: css`
                                    max-width: initial;
                                    width: 412px;
                                    padding: 17px;
                                    box-shadow: 0 2px 2px 0 rgb(0 0 0 / 33%);
                                    border-radius: 0;
                                `,
                            }}
                            passAsChildren={true}
                        >
                            <MyAccountOverlay />
                        </Tooltip>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper>
                        {/* Sign In */}
                        <Tooltip
                            trigger="mouseenter focus"
                            onOpen={({ instance }: any) => {
                                setTippyInstance(instance);
                                setIsOpen(true);
                            }}
                            onClose={({ instance }: any) => {
                                setTippyInstance(instance);
                                setIsOpen(false);
                            }}
                            placement="bottom-end"
                            triggerAltText={translate("Sign In Menu")}
                            triggerComponent={SignInElement}
                            delay={[100, 350]}
                            offset={[0, 8]}
                            cssOverrides={{
                                tooltipBody: css``,
                                tooltipContainer: css`
                                    max-width: initial;
                                    width: 308px;
                                    padding: 17px;
                                    box-shadow: 0 2px 2px 0 rgb(0 0 0 / 33%);
                                    border-radius: 0;
                                `,
                            }}
                            passAsChildren={true}
                        >
                            <SignInOverlay />
                        </Tooltip>
                    </StyledWrapper>
                )}
            </Hidden>
            <Hidden above="md">
                <MobileElement />
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overrideTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "openInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
